/**
 * MyTasksDialog — glanceable cross-module to-do for the signed-in user.
 *
 * Aggregates every open item assigned to them across Project-Management
 * cards, Snagging items, the Pacer roadmap, and area Enhancements /
 * Milestones. Data comes from `GET /api/me/todo`, which filters by the
 * caller's email/name server-side, so the dialog just renders and
 * deep-links into whichever module owns each item.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ListChecks, Loader2, Trello, AlertOctagon, GitBranch, Wrench, Flag, ExternalLink, CalendarClock,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULE_META = {
  pm: { icon: Trello, color: '#1F4FBE', label: 'Card' },
  snag: { icon: AlertOctagon, color: '#B91C1C', label: 'Snag' },
  pacer: { icon: GitBranch, color: '#0F766E', label: 'Pacer' },
  enhancement: { icon: Wrench, color: '#B45309', label: 'Enhancement' },
  milestone: { icon: Flag, color: '#8B5CF6', label: 'Milestone' },
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'pm', label: 'Cards' },
  { key: 'snag', label: 'Snags' },
  { key: 'pacer', label: 'Pacer' },
  { key: 'enhancement', label: 'Enhancements' },
  { key: 'milestone', label: 'Milestones' },
];

function dueMeta(due) {
  if (!due) return null;
  const d = new Date(due);
  if (isNaN(d.getTime())) return { text: due, overdue: false };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = new Date(d);
  day.setHours(0, 0, 0, 0);
  const diff = Math.round((day.getTime() - today.getTime()) / 86400000);
  if (diff < 0) return { text: `${Math.abs(diff)}d overdue`, overdue: true };
  if (diff === 0) return { text: 'Due today', overdue: false };
  if (diff === 1) return { text: 'Due tomorrow', overdue: false };
  if (diff <= 7) return { text: `Due in ${diff}d`, overdue: false };
  return { text: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }), overdue: false };
}

export default function MyTasksDialog({ open, onClose, adminToken }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(null);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!open || !adminToken) return;
    setLoading(true);
    setErr('');
    setFilter('all');
    axios
      .get(`${API}/me/todo`, { headers: { Authorization: `Bearer ${adminToken}` } })
      .then((r) => {
        setItems(r.data?.items || []);
        setCounts(r.data?.counts || null);
      })
      .catch((e) => setErr(e?.response?.data?.detail || 'Failed to load your to-do'))
      .finally(() => setLoading(false));
  }, [open, adminToken]);

  const openItem = (item) => {
    if (item.module === 'pm' && item.board_id) {
      window.location.href = `/pm?board=${item.board_id}&card=${item.id}`;
      return;
    }
    if (item.module === 'pacer') {
      window.location.href = `/admin/dashboard?tab=pacer&task=${item.id}`;
      return;
    }
    if (item.module === 'snag') {
      onClose();
      window.dispatchEvent(new CustomEvent('atrium:open-snagging'));
      return;
    }
    if ((item.module === 'enhancement' || item.module === 'milestone') && item.area_id) {
      window.dispatchEvent(new CustomEvent('open-area', { detail: { areaId: item.area_id } }));
      onClose();
    }
  };

  const shown = useMemo(
    () => (filter === 'all' ? items : items.filter((i) => i.module === filter)),
    [items, filter],
  );
  const total = counts?.total || 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="my-tasks-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <ListChecks className="w-5 h-5 text-blue-600" />
            My To-Do
            {total > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs">{total}</span>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Open items assigned to you across cards, snags, the Pacer roadmap, and area work.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-8 text-gray-500" data-testid="my-tasks-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your to-do…
          </div>
        )}

        {!loading && err && (
          <div className="text-sm text-red-600 py-4" data-testid="my-tasks-error">{err}</div>
        )}

        {!loading && !err && (
          <>
            {/* Filter chips */}
            <div className="flex flex-wrap gap-2 mb-3" data-testid="my-tasks-filters">
              {FILTERS.map((f) => {
                const n = f.key === 'all' ? total : counts?.[f.key];
                const active = filter === f.key;
                return (
                  <button
                    key={f.key}
                    type="button"
                    data-testid={`my-tasks-filter-${f.key}`}
                    onClick={() => setFilter(f.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                      active
                        ? 'bg-[#0A1A2F] text-white border-[#0A1A2F]'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    {f.label}{n ? ` ${n}` : ''}
                  </button>
                );
              })}
            </div>

            {shown.length === 0 ? (
              <div className="text-center py-10 text-gray-500" data-testid="my-tasks-empty">
                <ListChecks className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">You&apos;re all caught up — no open items assigned to you here.</p>
              </div>
            ) : (
              <ul className="space-y-2" data-testid="my-tasks-list">
                {shown.map((item, i) => {
                  const meta = MODULE_META[item.module] || MODULE_META.pm;
                  const Icon = meta.icon;
                  const due = dueMeta(item.due_date);
                  return (
                    <li
                      key={`${item.module}-${item.id}-${i}`}
                      data-testid={`my-task-${item.module}-${i}`}
                      className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50 hover:bg-blue-50 transition-colors cursor-pointer"
                      onClick={() => openItem(item)}
                    >
                      <div
                        className="flex items-center justify-center w-8 h-8 rounded-md shrink-0"
                        style={{ backgroundColor: `${meta.color}18` }}
                      >
                        <Icon className="w-4 h-4" style={{ color: meta.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-900 truncate">{item.title || 'Untitled'}</p>
                        <p className="text-xs text-gray-500 truncate">
                          <span className="font-bold uppercase tracking-wide" style={{ color: meta.color }}>
                            {meta.label}
                          </span>
                          <span className="mx-1">·</span>
                          {item.context}
                          {item.severity ? <span className="ml-1">· {item.severity}</span> : null}
                        </p>
                      </div>
                      {due && (
                        <span
                          className={`shrink-0 flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full ${
                            due.overdue ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          <CalendarClock className="w-3 h-3" />
                          {due.text}
                        </span>
                      )}
                      <ExternalLink className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
